import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { PALETTE } from '../palette.js';
import { Button, Card } from './primitives.jsx';
import Select from './Select.jsx';
import { checkedWorkspaceDefaults, workspaceDefaultsBody, workspaceTimeZones } from '../company-workspace-defaults.js';

export default function CompanyWorkspaceDefaults({ companyId, role, onEditStateChange }) {
  const [defaults, setDefaults] = useState(null);
  const [savedValues, setSavedValues] = useState('');
  const signature = value => value ? JSON.stringify([value.countryCode, value.timeZone]) : '';
  const acceptDefaults = value => { setDefaults(value); setSavedValues(signature(value)); };
  const [status, setStatus] = useState('loading');
  const [notice, setNotice] = useState('');
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  const allowed = ['owner', 'admin'].includes(role);
  const headers = { 'x-company-id': companyId };

  useEffect(() => {
    const current = ++generation.current;
    let alive = true;
    setDefaults(null); setNotice(''); setStatus('loading');
    if (!companyId || !allowed) { setStatus('hidden'); return; }
    const controller = new AbortController(); const timer = setTimeout(() => controller.abort(), 15000);
    (async () => {
      let enabled = false;
      try {
        const config = await api.get('/api/saas/registration/config', { signal: controller.signal }).catch(() => null);
        enabled = config?.enabled === true && config.contractVersion === 'self_service_registration_v1';
        const loaded = checkedWorkspaceDefaults(await api.get('/api/saas/company/defaults', { headers, signal: controller.signal }), companyId);
        if (!alive || generation.current !== current) return;
        if (!enabled && loaded.revision === 0) { setStatus('hidden'); return; }
        acceptDefaults({ ...loaded, countryCode: loaded.countryCode || 'CA', timeZone: loaded.timeZone || 'America/Toronto' });
        setStatus('ready');
      } catch {
        if (!alive || generation.current !== current) return;
        setStatus(enabled ? 'error' : 'hidden');
        setNotice('Workspace defaults couldn’t be loaded. Try again.');
      } finally { clearTimeout(timer); }
    })();
    return () => { alive = false; generation.current++; controller.abort(); clearTimeout(timer); };
  }, [companyId, allowed, reload]);

  const dirty = Boolean(defaults && savedValues && signature(defaults) !== savedValues);
  useEffect(() => {
    onEditStateChange?.({ dirty, busy: status === 'saving' });
    return () => onEditStateChange?.({ dirty: false, busy: false });
  }, [dirty, status, onEditStateChange]);

  async function save(event) {
    event.preventDefault();
    if (!allowed || status === 'saving' || !defaults || defaults.companyId !== companyId) return;
    const current = generation.current;
    setStatus('saving'); setNotice('');
    const controller = new AbortController(); const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const saved = checkedWorkspaceDefaults(await api.patch('/api/saas/company/defaults', workspaceDefaultsBody(defaults), { headers, signal: controller.signal }), companyId);
      if (generation.current !== current) return;
      if (saved.revision <= defaults.revision) throw new Error('invalid_workspace_defaults');
      acceptDefaults(saved); setStatus('ready'); setNotice('Workspace defaults saved.');
    } catch (error) {
      if (generation.current !== current) return;
      if (error.code === 'company_defaults_conflict') {
        try {
          const latest = checkedWorkspaceDefaults(await api.get('/api/saas/company/defaults', { headers, signal: controller.signal }), companyId);
          if (generation.current !== current) return;
          acceptDefaults({ ...latest, countryCode: latest.countryCode || 'CA', timeZone: latest.timeZone || 'America/Toronto' });
          setStatus('ready'); setNotice('These defaults changed. Review the latest values before saving.');
          return;
        } catch {
          if (generation.current !== current) return;
          setDefaults(null); setStatus('error'); setNotice('Workspace defaults couldn’t be loaded. Try again.');
          return;
        }
      }
      setStatus('ready'); setNotice('Workspace defaults couldn’t be saved. Try again.');
    } finally { clearTimeout(timer); }
  }

  // Existing production companies have no defaults row and keep their current UI.
  if (!allowed || status === 'hidden' || status === 'loading') return null;
  return <Card style={{ padding: 24, marginTop: 18 }}>
    <h2 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 650 }}>Workspace defaults</h2>
    <p style={{ margin: '0 0 18px', fontSize: 12, color: PALETTE.ink3 }}>Set your company’s country and time zone.</p>
    {defaults ? <form onSubmit={save} className="bl-workspace-defaults-form">
      <label htmlFor="workspace-country">Country
        <Select id="workspace-country" value={defaults.countryCode} disabled={status === 'saving'} onChange={countryCode => { setDefaults({ ...defaults, countryCode }); setNotice(''); }}
          options={[{ value: 'CA', label: 'Canada' }, { value: 'US', label: 'United States' }]} />
      </label>
      <label htmlFor="workspace-timezone">Time zone
        <Select id="workspace-timezone" value={defaults.timeZone} disabled={status === 'saving'} onChange={timeZone => { setDefaults({ ...defaults, timeZone }); setNotice(''); }}
          options={workspaceTimeZones(defaults.timeZone).map(zone => ({ value: zone, label: zone }))} />
      </label>
      <Button type="submit" variant="accent" size="sm" disabled={status === 'saving'} style={{ minHeight: 42, alignSelf: 'end' }}>{status === 'saving' ? 'Saving…' : 'Save defaults'}</Button>
    </form> : <Button variant="secondary" size="sm" onClick={() => setReload(value => value + 1)}>Try again</Button>}
    <p aria-live="polite" style={{ minHeight: 18, marginBottom: 0, fontSize: 12 }}>{notice}</p>
  </Card>;
}
